import React, {useEffect} from 'react';
import {Typography} from '@mui/material';
import ProjectHeader from '../components/project/ProjectHeader';
import ProjectTextBox from '../components/project/ProjectTextBox';
import ProjectImage from '../components/project/ProjectImage';
import ProjectTwoColumns from '../components/project/ProjectTwoColumns';
import ProjectPersona from '../components/project/ProjectPersona';
import ProjectQuestions from '../components/project/ProjectQuestions';
import Footer from '../components/Footer';

function WhenItDrop() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div>
            <ProjectHeader
                backgroundImage="/assets/images/cover.wid.png"
                projectName="WhenItDrop"
                role="UX/UI Designer"
                duration="Mar – Sep 2020"
            />

            <ProjectTwoColumns>
                <div>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>Project Vision</Typography>
                    <Typography variant="body1">
                        <strong>WhenItDrop</strong> is a Web app that tracks Amazon's products prices, alerting users in real-time when a product price decreases.
                    </Typography>
                </div>
                <div>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>Challenges</Typography>
                    <Typography variant="body1">
                        Create a minimal user interface focused on product search and management of the notifications of each followed product.
                    </Typography>
                </div>
            </ProjectTwoColumns>

            <ProjectImage url="/assets/images/desktop-mobile-wid.png" alt="WhenItDrop desktop and mobile views"/>

            <ProjectTextBox withBackground>
                <Typography variant="h5" fontWeight="bold">Kickoff</Typography>
                <Typography variant="body1">
                    In this project, I conducted <strong>user interviews</strong>, which I then turned into empathy maps to better understand the target user and their needs.
                    I found that some users don't get in time to take advantage of discounts dedicated to the products they are interested in.
                </Typography>
                <Typography variant="body1">
                    I designed a cross-platform app that allows users to search products and add them to a list of favorite products. After that, the users will receive a notification every time that a price drop occurs.
                </Typography>
                <ProjectQuestions questions={[
                    'What is the product and who is it for?',
                    'What do our users need most?',
                    'What challenges could we face moving forward?',
                    'Who do we see as our biggest competitors?',
                ]}/>
            </ProjectTextBox>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Affinity Diagram</Typography>
                <Typography variant="body1">
                    I used an <strong>affinity diagram</strong> to discover the user's needs and this led me to an improvement in user experience, outlining their goals.
                </Typography>
                <ProjectImage url="/assets/images/affinity diagram.wid.png" alt="Affinity diagram"/>
            </ProjectTextBox>

            <ProjectTwoColumns withBackground title="Users">
                <div>
                    <ProjectPersona url="/assets/images/luis.png" name="Primary">
                        <li><strong>Name:</strong> Luis</li>
                        <li><strong>Age:</strong> 29</li>
                        <li><strong>Occupation:</strong> Software engineer</li>
                    </ProjectPersona>
                    <Typography variant="body1">
                        Luis works hard in a company in his city. After a day of work, he likes to play video games to relieve the stress. Being a fan of gaming and collector, he would like to buy the PlayStation 5 but unfortunately, it's hard to find.
                    </Typography>
                </div>
                <div>
                    <ProjectPersona url="/assets/images/paola.png" name="Secondary">
                        <li><strong>Name:</strong> Paola</li>
                        <li><strong>Age:</strong> 40</li>
                        <li><strong>Occupation:</strong> Teacher</li>
                    </ProjectPersona>
                    <Typography variant="body1">
                        She loves two things in her life: children and cooking. Due to various commitments, during the day she does not cook much and she would like to buy a kitchen robot to be faster.
                    </Typography>
                </div>
            </ProjectTwoColumns>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Competitive Analysis</Typography>
                <Typography variant="body1">
                    I created an <strong>audit competitive</strong>, examining two potential competitors, and I discovered that their characteristics are similar:
                </Typography>
                <Typography variant="body1">~ Paid version / Free version</Typography>
                <Typography variant="body1">~ Difficult to access</Typography>
                <Typography variant="body1">~ Distracting Interface</Typography>
                <Typography variant="body1">~ Requires browser plug-in</Typography>
                <ProjectImage url="/assets/images/audit.wid.png" alt="Competitive audit"/>
            </ProjectTextBox>

            <ProjectTextBox withBackground>
                <Typography variant="h5" fontWeight="bold">Sitemap</Typography>
                <Typography variant="body1">
                    I created a journey map to identify possible points of pain and improvement opportunities. Difficulty with website navigation, together with notification settings, were points of pain for most users.
                </Typography>
                <Typography variant="body1">
                    My goal was to make strategic information architecture decisions that would improve overall website navigation. Later, I created a <strong>sitemap</strong> to list all website's pages.
                </Typography>
                <ProjectImage url="/assets/images/journeymap.wid.png" alt="Journey map"/>
            </ProjectTextBox>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Wireframe</Typography>
                <Typography variant="body1">
                    During the transition from paper to digital <strong>wireframe</strong>, it was easy to understand how design could help address user pain points and improve user experience. Making a minimal product card was one of my main goals.
                </Typography>
            </ProjectTextBox>

            <ProjectImage url="/assets/images/wireframe-wid.png" alt="Wireframes"/>

            <ProjectTextBox withBackground>
                <Typography variant="h5" fontWeight="bold">Interaction</Typography>
                <Typography variant="body1">
                    After creating my prototype from low fidelity wireframes, I asked 5 users to try the web app in hopes of garnering enough <strong>feedback</strong> to use for my next set of design interactions.
                </Typography>
            </ProjectTextBox>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Mockups</Typography>
                <Typography variant="body1">
                    Based on the insights from the usability study, I made some changes to improve the tracking of Amazon's products prices. One of the changes I made was the addition of the <strong>"desired price"</strong> option in the product card. This allowed users to receive a notification only when the price of the product was less than or equal to the one desired.
                </Typography>
                <Typography variant="body1">
                    Another change I made was to give the user the option to choose easily the <strong>"notification options"</strong>, within the product card (New or Used and Amazon or Amazon and Third Parties).
                </Typography>
            </ProjectTextBox>

            <ProjectImage url="/assets/images/mockup.wid.png" alt="Mockups"/>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Design System</Typography>
                <ProjectImage url="/assets/images/designsystem.wid.png" alt="Design system"/>
            </ProjectTextBox>

            <ProjectImage url="/assets/images/secondmockups.wid.png" alt="Additional mockups"/>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Takeaways</Typography>
                <Typography variant="body1">
                    <strong>WhenItDrop</strong> was my first web app designed at the beginning of this journey into the world of UX/UI Design. I had the opportunity to work with the founder of the project, which managed the development part.
                </Typography>
                <Typography variant="body1">
                    This allowed me, not only to improve my knowledge of the sector but also to understand how important it is to collaborate with a developer even after the project was handed over to him.
                </Typography>
            </ProjectTextBox>

            <Footer/>
        </div>
    );
}

export default WhenItDrop;
